import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, CheckCircle, SpeakerHigh, CaretDown } from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "../../lib/api";

const CHUNK = 1024 * 1024;
const MAX = 20 * 1024 * 1024;

async function uploadInChunks(key, file, onProgress) {
  const total = Math.ceil(file.size / CHUNK);
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  let last;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("total", String(total));
    fd.append("filename", file.name);
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), `${i}.part`);
    last = (await api.post(`/admin/audio/${key}/chunk`, fd)).data;
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return last.audio;
}

function Script({ script }) {
  return (
    <div className="mt-3 space-y-1.5 text-sm leading-relaxed text-foreground/80">
      {script.map(([sp, line], i) => (
        <p key={i}><span className="font-mono-fig text-xs text-indigo mr-2">{sp === "M" ? "Man" : "Woman"}:</span>{line}</p>
      ))}
    </div>
  );
}

function RecordingRow({ rec, onChange }) {
  const input = useRef(null);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const audio = rec.audio;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.mp3$/i.test(file.name) && file.type !== "audio/mpeg") return toast.error("Please choose an MP3 file.");
    if (file.size > MAX) return toast.error("File is larger than 20 MB.");
    try {
      setProgress(0);
      const saved = await uploadInChunks(rec.key, file, setProgress);
      onChange(rec.key, saved);
      toast.success("Recording uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/audio/${rec.key}`);
      onChange(rec.key, null);
      toast.success("Recording removed — browser voice will be used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <div className="neu-sm rounded-2xl p-4" data-testid={`admin-audio-row-${rec.key}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} className="flex-1 min-w-[200px] text-left flex items-start gap-2" data-testid={`admin-audio-toggle-${rec.key}`}>
          <CaretDown size={14} className={`mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="text-sm text-foreground font-medium">{rec.label}</span>
        </button>
        {audio ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`admin-audio-status-${rec.key}`}>
            <CheckCircle size={14} weight="fill" /> MP3 uploaded
          </span>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid={`admin-audio-status-${rec.key}`}>Browser voice</span>
        )}
        <input ref={input} type="file" accept=".mp3,audio/mpeg" className="hidden" onChange={pick} data-testid={`admin-audio-file-${rec.key}`} />
        <button
          onClick={() => input.current?.click()}
          disabled={progress !== null}
          data-testid={`admin-audio-upload-${rec.key}`}
          className="neu-btn-primary rounded-full px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          <UploadSimple size={14} weight="bold" />
          {progress !== null ? `${progress}%` : audio ? "Replace" : "Upload MP3"}
        </button>
        {audio && (
          <button onClick={remove} data-testid={`admin-audio-delete-${rec.key}`} className="neu-btn rounded-full p-2" aria-label="Remove recording">
            <Trash size={14} />
          </button>
        )}
      </div>
      {audio && (
        <audio controls preload="none" className="mt-3 w-full h-9" src={`${API}/audio/${rec.key}?v=${audio.version}`} data-testid={`admin-audio-player-${rec.key}`} />
      )}
      {open && (
        <div className="mt-2 pl-6">
          {rec.context && <p className="text-xs text-muted-foreground italic">{rec.context}</p>}
          <Script script={rec.script} />
        </div>
      )}
    </div>
  );
}

export const AudioManager = () => {
  const [recs, setRecs] = useState(null);

  useEffect(() => {
    api.get("/admin/audio").then((r) => setRecs(r.data)).catch(() => toast.error("Could not load recordings"));
  }, []);

  const groups = useMemo(() => {
    const g = {};
    (recs || []).forEach((r) => {
      const k = `${r.part_no}-${r.version}`;
      (g[k] ||= { title: `Part ${r.part_no} · Version ${r.version} · ${r.part_title}`, items: [] }).items.push(r);
    });
    return Object.entries(g);
  }, [recs]);

  const onChange = (key, audio) => setRecs((rs) => rs.map((r) => (r.key === key ? { ...r, audio } : r)));
  const uploaded = (recs || []).filter((r) => r.audio).length;

  if (!recs) return <div className="font-mono-fig text-sm text-muted-foreground p-6">Loading recordings…</div>;

  return (
    <div className="mt-8 neu rounded-3xl p-6 md:p-8" data-testid="admin-audio-manager">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline flex items-center gap-2"><SpeakerHigh size={12} weight="duotone" /> Listening audio</div>
          <p className="mt-2 text-sm text-foreground/75 max-w-2xl">
            Upload an MP3 (max 20 MB) for any recording. Open a row to see the script to record. Recordings without an MP3 use the browser voice.
          </p>
        </div>
        <div className="neu-inset-sm rounded-full px-4 py-2 text-sm font-mono-fig" data-testid="admin-audio-count">
          {uploaded} / {recs.length} uploaded
        </div>
      </div>
      <div className="mt-6 space-y-8">
        {groups.map(([k, g]) => (
          <div key={k}>
            <h3 className="text-sm font-bold text-foreground mb-3">{g.title}</h3>
            <div className="space-y-3">
              {g.items.map((r) => <RecordingRow key={r.key} rec={r} onChange={onChange} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
